import { t } from "ttag";

import { databaseApi, fieldApi, tableApi } from "metabase/api";
import type { DispatchFn } from "metabase/lib/redux";
import { checkNotNull } from "metabase/lib/types";
import { getMetadata } from "metabase/selectors/metadata";
import * as Lib from "metabase-lib";
import Question from "metabase-lib/v1/Question";
import type { DatabaseId } from "metabase-types/api";
import type { GetState } from "metabase-types/store";

const queryOptions = { subscribe: false, forceRefetch: true };

export const prepareDatabaseSwitch =
  (question: Question, databaseId: DatabaseId) =>
  async (dispatch: DispatchFn, getState: GetState) => {
    const query = question.query();
    const fieldIds = Lib.nativeQueryFieldIds(query);
    const targetDatabase = await dispatch(
      databaseApi.endpoints.getDatabase.initiate(
        { id: databaseId },
        queryOptions,
      ),
    ).unwrap();
    if (targetDatabase.engine !== Lib.engine(query)) {
      throw new Error(
        t`Automatic field mapping requires databases with the same engine.`,
      );
    }
    if (
      Object.values(Lib.templateTags(query)).some((tag) => tag.type === "card")
    ) {
      throw new Error(
        t`This query references a saved question. Update that reference before switching databases.`,
      );
    }

    const fields = await Promise.all(
      fieldIds.map((id) =>
        dispatch(
          fieldApi.endpoints.getField.initiate({ id }, queryOptions),
        ).unwrap(),
      ),
    );
    const sourceTables = await Promise.all(
      [...new Set(fields.map((field) => field.table_id))].map((id) =>
        dispatch(
          tableApi.endpoints.getTableQueryMetadata.initiate(
            { id, include_hidden_fields: true },
            queryOptions,
          ),
        ).unwrap(),
      ),
    );
    const schemas = [
      ...new Set(sourceTables.map((table) => table.schema ?? "")),
    ];
    const tableLists = await Promise.all(
      schemas.map((schema) =>
        dispatch(
          databaseApi.endpoints.listDatabaseSchemaTables.initiate(
            { id: databaseId, schema },
            queryOptions,
          ),
        ).unwrap(),
      ),
    );
    const targetTables = await Promise.all(
      sourceTables.map((sourceTable) => {
        const targetTable = Lib.matchingNativeQueryTable(
          sourceTable,
          databaseId,
          tableLists.flat(),
        );
        return dispatch(
          tableApi.endpoints.getTableQueryMetadata.initiate(
            { id: targetTable.id, include_hidden_fields: true },
            queryOptions,
          ),
        ).unwrap();
      }),
    );

    const metadata = getMetadata(getState());
    const mappedQuery = Lib.withMappedNativeQueryDatabase(
      query,
      databaseId,
      Lib.metadataProvider(databaseId, metadata),
      sourceTables,
      targetTables,
    );
    return checkNotNull(
      new Question(question.card(), metadata, question._parameterValues)
        .setQuery(mappedQuery)
        .legacyNativeQuery(),
    ).setDefaultCollection();
  };
